<template>
  <action-menu :menuOptions="kebabOptions" iconType="kebab" />
</template>

<script lang="ts">
import { sendSegmentEvent } from '../../../../lib/segment';
import { sitterDetailsForParent } from '@/entrypoints/messages';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';

import ActionMenu from '@/components/action_menu/ActionMenu.vue';
import { MenuItemOptions } from '@/components/action_menu/__types__/ActionMenuTypes';

import ApplicationIcon from '@/graphics/icons/ApplicationIcon.vue';
import StarOutlineIcon from '@/graphics/icons/StarOutlineIcon.vue';
import AddAvailabilityIcon from '@/graphics/icons/AddAvailabilityIcon.vue';
import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import TrustAndSafetyIcon from '@/graphics/icons/TrustAndSafetyIcon.vue';

export default {
  name: 'KebabMenu',
  components: {
    ActionMenu,
  },
  props: {
    correspondingUserAccountId: {
      type: String,
      required: true,
    },
    correspondingUserId: {
      type: String,
      required: true,
    },
    accountType: {
      type: String,
      required: true,
    },
    currentChannelUrl: {
      type: String,
      required: true,
    },
  },
  data(): any {
    return {
      canReview: false,
      sitterHasVisibleProfile: true,
      sitterProviderType: null,
    };
  },
  computed: {
    kebabOptions(): MenuItemOptions[] {
      return this.accountType === 'sitter' ? this.getSitterOptions() : this.getParentOptions();
    },
    supportTicketHref(): string {
      const channelUrlParam = this.currentChannelUrl ? `&channel_url=${this.currentChannelUrl}` : '';
      return `https://support.sittercity.com/hc/en-us/requests/new?&ticket_form_id=360000078194${channelUrlParam}`;
    },
  },
  watch: {
    correspondingUserId() {
      if (this.accountType === 'sitter') return;
      this.updateSitterDetails();
    },
  },
  mounted() {
    this.updateSitterDetails();
  },
  methods: {
    async updateSitterDetails() {
      const details = await sitterDetailsForParent(this.correspondingUserId);

      this.canReview = details.canReview;
      this.sitterHasVisibleProfile = details.hasVisibleProfile;
      this.sitterProviderType = details.providerType;
    },
    getSitterOptions(): MenuItemOptions[] {
      return [
        {
          icon: ApplicationIcon,
          height: 24,
          width: 20,
          iconClasses: 'mr-18 ml-2',
          labelText: 'View open jobs',
          href: `/open_jobs/${this.correspondingUserAccountId}`,
          target: '_blank',
          click: () => {},
        },
        {
          icon: StarOutlineIcon,
          height: 24,
          width: 24,
          labelText: 'Request a review',
          href: '/provider/reviews/request',
          target: '_self',
          click: () => {},
        },
        this.getReportOption(),
      ];
    },
    getParentOptions(): MenuItemOptions[] {
      const options: MenuItemOptions[] = [];

      if (this.sitterHasVisibleProfile) {
        const viewProfileOption = this.getViewProfileOption();
        if (viewProfileOption) {
          options.push(viewProfileOption);
        }
        options.push(this.getScheduleInterviewOption());
      }

      if (this.canReview) {
        const writeReviewOption = this.getWriteReviewOption();
        if (writeReviewOption) {
          options.push(writeReviewOption);
        }
      }

      options.push(this.getBackgroundCheckOption(), this.getReportOption());

      return options;
    },
    getViewProfileOption(): MenuItemOptions | null {
      const profileUrl = getProviderProfileUrl(this.correspondingUserId, null, this.sitterProviderType);
      if (!profileUrl) {
        return null;
      }
      return {
        icon: ApplicationIcon,
        height: 24,
        width: 20,
        iconClasses: 'mr-18 ml-2',
        labelText: 'View profile',
        href: profileUrl,
        target: '_blank',
        click: () => {
          sendSegmentEvent('Viewed Profile in Message', {
            viewed_profile_id: this.correspondingUserId,
          });
        },
      };
    },
    getScheduleInterviewOption(): MenuItemOptions {
      return {
        icon: AddAvailabilityIcon,
        height: 24,
        width: 24,
        iconClasses: 'availability-icon',
        labelText: 'Schedule interview',
        href: `/interview-request/${this.correspondingUserId}/new`,
        target: '_self',
        click: () => {},
      };
    },
    getWriteReviewOption(): MenuItemOptions | null {
      if (!this.correspondingUserId) {
        return null;
      }
      return {
        icon: StarOutlineIcon,
        height: 24,
        width: 24,
        labelText: 'Write a review',
        href: `/ratings/sitters/${this.correspondingUserId}/new`,
        target: '_self',
        click: () => {},
      };
    },
    getBackgroundCheckOption(): MenuItemOptions {
      return {
        icon: TrustAndSafetyIcon,
        height: 24,
        width: 24,
        labelText: 'Run background check',
        href: `/background-check/sponsored-checks/${this.correspondingUserAccountId}/new`,
        target: '_self',
        click: () => {
          sendSegmentEvent('Run Background Check in Message', {
            requested_user_id: this.correspondingUserAccountId,
          });
        },
      };
    },
    getReportOption(): MenuItemOptions {
      return {
        icon: WarningOutlineIcon,
        height: 24,
        width: 24,
        labelText: 'Report',
        href: this.supportTicketHref,
        target: '_self',
        click: () => {},
      };
    },
  },
};
</script>
